import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "../lib/api";
import SectionHeader from "../components/SectionHeader";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";

export default function ContactPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setOk(true);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", company: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("contact.error"));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="contact-page" className="pb-24">
      <section className="border-b border-slate-800 py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader kicker="GET IN TOUCH" title={t("contact.title")} description={t("contact.subtitle")} testIdPrefix="contact" />
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-10">
          <aside className="lg:col-span-2 space-y-6" data-testid="contact-info">
            <ContactItem icon={Mail} label="EMAIL" value="support@olivine-global.com" href="mailto:support@olivine-global.com" testid="contact-info-email" />
            <ContactItem icon={Phone} label="PHONE" value="+91 8376008218" href="tel:+918376008218" testid="contact-info-phone" />
            <ContactItem icon={MapPin} label="HEADQUARTERS" value="Sector 65, Golf Course Extension Road, Gurgaon – 122018, India" testid="contact-info-address" />

            <div className="mt-8 p-6 border border-amber-500/30 bg-slate-950">
              <div className="font-mono-spec text-[10px] text-amber-400">RESPONSE SLA</div>
              <div className="mt-2 font-display font-bold text-white">Within 1 business day.</div>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">Every message reaches our engineering desk — not a call center.</p>
            </div>
          </aside>

          <form onSubmit={submit} className="lg:col-span-3 border border-slate-800 p-8 bg-slate-950" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label={t("contact.name")} testid="contact-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <Field label={t("contact.email")} testid="contact-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
              <Field label={t("contact.company")} testid="contact-company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
              <Field label={t("contact.phone")} testid="contact-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            </div>
            <div className="mt-5">
              <Field label={t("contact.subject")} testid="contact-subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />
            </div>
            <div className="mt-5">
              <label className="block">
                <span className="text-xs font-mono-spec text-slate-400">{t("contact.message")} <span className="text-amber-400">*</span></span>
                <textarea
                  data-testid="contact-message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-1 w-full bg-slate-900 border border-slate-800 focus:border-amber-500 focus:ring-0 outline-none text-white p-3 rounded-sm text-sm"
                />
              </label>
            </div>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={submitting}
              className="mt-6 inline-flex items-center gap-2 bg-amber-500 text-slate-950 font-bold px-6 py-3 hover:bg-amber-400 transition-colors rounded-sm disabled:opacity-60"
            >
              {submitting ? "Sending…" : t("contact.submit")} <Send size={14} />
            </button>
            {ok && <div className="mt-4 text-sm text-amber-400 font-mono-spec" data-testid="contact-success">{t("contact.success")}</div>}
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs font-mono-spec text-slate-400">{label}{required && <span className="text-amber-400"> *</span>}</span>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-slate-900 border border-slate-800 focus:border-amber-500 focus:ring-0 outline-none text-white p-3 rounded-sm text-sm"
      />
    </label>
  );
}

function ContactItem({ icon: Icon, label, value, href, testid }) {
  const content = (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 flex items-center justify-center border border-slate-800 bg-slate-950">
        <Icon size={16} className="text-amber-400" />
      </div>
      <div>
        <div className="font-mono-spec text-[10px] text-slate-400">{label}</div>
        <div className="mt-1 text-white">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} data-testid={testid} className="block hover:text-amber-400 transition-colors">{content}</a> : <div data-testid={testid}>{content}</div>;
}
